// Favicon discovery: given a sender's domain, find the best icon its website publishes.
// Pure functions; the platform does the fetching (Electron main process, iOS URLSession) and caching.

const MULTI_PART_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'me.uk', 'com.au', 'net.au', 'org.au', 'edu.au', 'co.nz', 'org.nz', 'co.jp', 'ne.jp',
  'or.jp', 'com.br', 'com.mx', 'com.ar', 'com.cn', 'com.hk', 'com.sg', 'com.tr', 'co.in', 'co.za', 'co.kr', 'com.tw', 'co.il',
]);

/** "mail.news.example.co.uk" -> "example.co.uk". */
export function registrableDomain(host: string): string {
  const parts = host.toLowerCase().replace(/\.$/, '').split('.');
  if (parts.length <= 2) return parts.join('.');
  const lastTwo = parts.slice(-2).join('.');
  return MULTI_PART_SUFFIXES.has(lastTwo) ? parts.slice(-3).join('.') : lastTwo;
}

/** Only public DNS names (no IP literals, localhost or private TLDs) are ever fetched. */
export function isFetchableDomain(domain: string): boolean {
  const d = domain.toLowerCase();
  if (d.length > 253 || !/^[a-z0-9.-]+\.[a-z]{2,63}$/.test(d)) return false;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(d)) return false;
  if (/(^|\.)(localhost|local|internal|intranet|lan|home|corp|test|example|invalid|onion)$/.test(d)) return false;
  return d.split('.').every((label) => label.length > 0 && label.length <= 63 && !label.startsWith('-') && !label.endsWith('-'));
}

/** Domains to try, most specific first: the sender host, then its registrable domain. */
export function faviconCandidates(domain: string): string[] {
  const d = domain.toLowerCase();
  const root = registrableDomain(d);
  const list = [d];
  if (root !== d) list.push(root);
  return list.filter(isFetchableDomain);
}

export interface IconLink {
  href: string;
  rel: string;
  sizes?: string;
  type?: string;
  score: number;
}

function attr(tag: string, name: string): string | undefined {
  const m = tag.match(new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i'));
  return m ? (m[2] ?? m[3] ?? m[4]) : undefined;
}

/** Extracts icon <link>s from a page and ranks them: SVG, then large PNG / apple-touch-icon, then anything else. */
export function extractIconLinks(html: string, pageUrl: string): IconLink[] {
  const head = html.slice(0, 200_000);
  const links: IconLink[] = [];
  for (const m of head.matchAll(/<link\b[^>]*>/gi)) {
    const tag = m[0];
    const rel = (attr(tag, 'rel') ?? '').toLowerCase();
    if (!/\bicon\b|apple-touch-icon|mask-icon/.test(rel)) continue;
    if (rel.includes('mask-icon')) continue; // monochrome Safari pinned-tab icons look wrong as avatars
    const raw = attr(tag, 'href');
    if (!raw || raw.startsWith('data:') && raw.length > 100_000) continue;
    let href: string;
    try {
      href = new URL(raw.replace(/&amp;/g, '&'), pageUrl).toString();
    } catch {
      continue;
    }
    const sizes = attr(tag, 'sizes');
    const type = attr(tag, 'type');
    const px = Math.max(0, ...(sizes ?? '').split(/\s+/).map((s) => parseInt(s.split('x')[0] ?? '0', 10) || 0));
    let score = 10;
    if (type?.includes('svg') || /\.svg(\?|$)/i.test(href)) score = 1000;
    else if (rel.includes('apple-touch-icon')) score = 500 + Math.min(px || 180, 256);
    else if (px) score = 100 + Math.min(px, 512) - (px > 256 ? (px - 256) / 4 : 0);
    else if (/\.png(\?|$)/i.test(href)) score = 60;
    links.push({ href, rel, sizes, type, score });
  }
  return links.sort((a, b) => b.score - a.score);
}

export function sniffImageType(bytes: Uint8Array): string | null {
  const b = bytes;
  if (b.length < 4) return null;
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image/png';
  if (b[0] === 0xff && b[1] === 0xd8) return 'image/jpeg';
  if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return 'image/gif';
  if (b[0] === 0x00 && b[1] === 0x00 && (b[2] === 0x01 || b[2] === 0x02) && b[3] === 0x00) return 'image/x-icon';
  if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57) return 'image/webp';
  const head = new TextDecoder().decode(b.subarray(0, Math.min(b.length, 512))).trimStart().toLowerCase();
  if (head.startsWith('<svg') || (head.startsWith('<?xml') && head.includes('<svg'))) return 'image/svg+xml';
  return null;
}
